import React from 'react';
import SEO from '../components/SEO';
import PageShell from '../components/site/PageShell';
import { brand } from '../config/esanzaContent';

const deletionSteps = [
  'Send an email to info@esanza.com from the email address connected to your eSanza account.',
  'Use the subject line: eSanza account deletion request.',
  'Include your full name, organization or tenant name if known, phone number if used in the app, and the type of data you want deleted.',
  'Our team will verify the request and confirm completion or any required retention reason by email.',
];

const deletedData = [
  'Mobile app and dashboard account profile data such as name, email address, phone number, role, and preferences.',
  'User-submitted mobile field records where deletion is legally and operationally possible.',
  'Uploaded photos or issue attachments linked only to the requesting user where deletion is permitted.',
];

const retainedData = [
  'Security logs, audit records, and access history may be retained for platform protection and compliance.',
  'Operational farm, traceability, training, VSLA, warehouse, or project records may be retained when they are required for legitimate business, legal, reporting, or field validation purposes.',
  'Backup copies may persist for a limited technical retention period before automatic rotation.',
];

const DataDeletionPage: React.FC = () => (
  <>
    <SEO
      title="Account and Data Deletion | eSanza"
      description="Request deletion of your eSanza account and associated app data."
    />
    <PageShell tone="light">
      <section className="relative overflow-hidden bg-[#06150e] px-5 pb-16 pt-32 text-white lg:px-8">
        <div className="absolute inset-0 bg-[linear-gradient(135deg,#06150e_0%,#123822_58%,#987a1f_140%)]" />
        <div className="relative mx-auto max-w-5xl">
          <p className="text-xs font-extrabold uppercase tracking-[0.28em] text-[#d4af37]">Account deletion</p>
          <h1 className="mt-5 text-4xl font-black tracking-tight md:text-6xl">Request deletion of eSanza account data.</h1>
          <p className="mt-6 max-w-3xl text-base leading-8 text-white/72">
            This page explains how authorized eSanza mobile app and dashboard users can request account deletion or deletion of associated personal data.
          </p>
        </div>
      </section>

      <section className="px-5 py-16 lg:px-8">
        <div className="mx-auto grid max-w-6xl gap-8 lg:grid-cols-[0.8fr_1.2fr]">
          <aside className="h-fit rounded-lg border border-[#d9dfd0] bg-white p-6 shadow-sm">
            <img src={brand.icon} alt="eSanza" className="h-14 w-14 rounded-xl" />
            <h2 className="mt-5 text-xl font-black text-[#102016]">{brand.name}</h2>
            <p className="mt-3 text-sm leading-6 text-[#5d6b61]">
              Developer: {brand.company}. This deletion request process applies to the eSanza mobile app, website, dashboard, and platform account data.
            </p>
            <a
              href={`mailto:${brand.email}?subject=eSanza account deletion request`}
              className="mt-6 inline-flex w-full justify-center rounded-lg bg-[#102016] px-5 py-3 text-sm font-black text-white hover:bg-[#174d2f]"
            >
              Email deletion request
            </a>
            <p className="mt-4 text-xs leading-5 text-[#6b766d]">
              We normally respond within 30 days after verifying the requester and the account.
            </p>
          </aside>

          <div className="space-y-5">
            <article className="rounded-lg border border-[#d9dfd0] bg-white p-6 shadow-sm">
              <h2 className="text-2xl font-black text-[#102016]">How to request deletion</h2>
              <ol className="mt-5 grid gap-3">
                {deletionSteps.map((step, index) => (
                  <li key={step} className="flex gap-4 rounded-lg bg-[#f1f4ec] p-4 text-sm leading-6 text-[#526057]">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#d4af37] text-xs font-black text-[#102016]">
                      {index + 1}
                    </span>
                    <span>{step}</span>
                  </li>
                ))}
              </ol>
            </article>

            <article className="rounded-lg border border-[#d9dfd0] bg-white p-6 shadow-sm">
              <h2 className="text-2xl font-black text-[#102016]">Data that may be deleted</h2>
              <div className="mt-4 space-y-3">
                {deletedData.map((item) => (
                  <p key={item} className="text-sm leading-7 text-[#5d6b61]">{item}</p>
                ))}
              </div>
            </article>

            <article className="rounded-lg border border-[#d9dfd0] bg-white p-6 shadow-sm">
              <h2 className="text-2xl font-black text-[#102016]">Data that may be retained</h2>
              <div className="mt-4 space-y-3">
                {retainedData.map((item) => (
                  <p key={item} className="text-sm leading-7 text-[#5d6b61]">{item}</p>
                ))}
              </div>
            </article>

            <article className="rounded-lg border border-[#d9dfd0] bg-[#102016] p-6 text-white shadow-sm">
              <h2 className="text-2xl font-black">Contact</h2>
              <p className="mt-4 text-sm leading-7 text-white/70">
                Send account or data deletion requests to{' '}
                <a href={`mailto:${brand.email}`} className="font-bold text-[#d4af37]">{brand.email}</a>.
              </p>
            </article>
          </div>
        </div>
      </section>
    </PageShell>
  </>
);

export default DataDeletionPage;
